import type { ISchedulerData, ISchedulerMarker, ITaskStockClaim } from '@interfaces/IScheduler';
import type { ISchedulerPlanData, ISchedulerPlanTask } from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import { createScheduleApi } from '@d-orca/shared/client/apis';
import { authedFetch, ProviderErrorResponse } from '@apis/auth';
import { ScheduleAlreadyImportedError, ScheduleSaveError, throwForError, throwForNotImported } from './scheduleErrors';

export { ProviderErrorResponse };
export {
  ScheduleSaveError,
  ScheduleNotImportedError,
  ScheduleAlreadyImportedError,
  ScheduleSiteUnavailableError,
  scheduleRetry,
  type ScheduleSaveErrorCode,
} from './scheduleErrors';
export type {
  ITaskProgressResponse,
  ICommittedTask,
  ICommitRejection,
  ICommitTaskForm,
  ITaskPlanEdit,
  ITaskPlanEditResult,
  IPlanWriteAck,
  ITaskStatusResult,
  CommitRejectionReason,
  ITaskReschedule,
  ITaskSpan,
} from '@d-orca/shared/client/apis';
export { TaskCommitRejectedError, TaskRescheduleConflictError } from '@d-orca/shared/client/apis';

// The shared schedule API (reads + granular execution writes) bound to the web
// transport (cookie `authedFetch`); mobile binds the same factory to its
// Bearer-sid transport. The scheduler-only operations below (save / import /
// delete / push-to-Genba) stay web-only.
export const {
  fetchSchedules,
  fetchSchedule,
  updateTaskProgress,
  setTaskAreaStatuses,
  commitTask,
  commitTasks,
  uncommitTask,
  setTaskStatus,
  editTaskPlan,
  addTaskComment,
  deleteTaskComment,
  setTaskCheck,
} = createScheduleApi(authedFetch);

/**
 * Seed a schedule into D-ORCA from Genba (one-time), or re-pull it with
 * `{ overwrite: true }` (a deliberate replace that discards local edits).
 * Returns the freshly-stored schedule (version '1' on a fresh seed). Without
 * `overwrite`, importing an already-seeded schedule throws
 * `ScheduleAlreadyImportedError` (409 SCHEDULE_ALREADY_IMPORTED).
 */
export async function importSchedule(
  siteId: string,
  scheduleNumber: string,
  { overwrite }: { overwrite?: boolean } = {},
): Promise<ISchedulerData> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/import`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(overwrite ? { overwrite: true } : {}),
    },
  );
  if (res.ok) {
    const data = (await res.json()) as ISchedulerData;
    if (typeof data.version !== 'string' || data.version === '') {
      throw new Error(
        'importSchedule: response is missing a valid version token — ' +
        'the backend shape may have changed.',
      );
    }
    return data;
  }
  const body = await res.clone().json().catch(() => null);
  if (res.status === 409 && body?.error?.code === 'SCHEDULE_ALREADY_IMPORTED') {
    throw new ScheduleAlreadyImportedError(scheduleNumber, body?.error?.message ?? 'already imported');
  }
  // Shared handling (reauth / not-connected / not-found / not-linked / 503 / generic).
  return throwForError(res, 'import schedule');
}

/** Vendor file formats the importer accepts. Shared between the API client and UI. */
export type ScheduleImportFormat = 'primavera-xer' | 'primavera-xml' | 'msproject-xml';

/** Result of a file import: the rendered schedule + the (possibly generated) number/name. */
export interface IScheduleFileImportResult {
  schedule: ISchedulerData;
  scheduleNumber: string;
  scheduleName: string;
}

/** Raw wire shape of the import-file response: ISchedulerData plus the resolved ids. */
interface IScheduleFileImportResponse extends ISchedulerData {
  scheduleNumber: string;
  scheduleName: string;
}

/**
 * Import a schedule from an uploaded vendor file (Primavera XER / P6 XML, MS
 * Project XML). The backend detects the format, maps it to the canonical model,
 * projects it onto the render model, and persists it as a D-ORCA-owned schedule
 * — so it lands in the picker and renders in the existing Gantt. Returns the
 * rendered schedule plus the resolved scheduleNumber so the caller can navigate.
 */
export async function importScheduleFromFile(
  siteId: string,
  file: File,
  opts: { format?: ScheduleImportFormat; scheduleNumber?: string; overwrite?: boolean } = {},
): Promise<IScheduleFileImportResult> {
  const form = new FormData();
  form.append('file', file);
  if (opts.format) form.append('format', opts.format);
  if (opts.scheduleNumber) form.append('scheduleNumber', opts.scheduleNumber);
  if (opts.overwrite) form.append('overwrite', 'true');

  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/schedules/import-file`, {
    method: 'POST',
    body: form,
  });
  if (res.ok) {
    const data = (await res.json()) as IScheduleFileImportResponse;
    if (typeof data.version !== 'string' || data.version === '') {
      throw new Error('importScheduleFromFile: response is missing a valid version token');
    }
    // `schedule` is the response minus the two id fields → structurally ISchedulerData.
    const { scheduleNumber, scheduleName, ...schedule } = data;
    return { schedule, scheduleNumber, scheduleName };
  }
  // Shared handling (reauth / not-connected / not-found / generic). A 422
  // (unparseable file) falls through to the generic Error the caller toasts.
  return throwForError(res, 'import schedule file');
}

/**
 * Create a new, empty D-ORCA-owned schedule (no tasks/groups) from just a name.
 * The backend generates the scheduleNumber and returns the same shape as the
 * file import, so the caller reuses its navigate-to-new-schedule handling. A
 * whitespace-only name is rejected server-side (400) and surfaces as a generic
 * Error the caller toasts.
 */
export async function createSchedule(
  siteId: string,
  scheduleName: string,
): Promise<IScheduleFileImportResult> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scheduleName }),
  });
  if (res.ok) {
    const data = (await res.json()) as IScheduleFileImportResponse;
    if (typeof data.version !== 'string' || data.version === '') {
      throw new Error('createSchedule: response is missing a valid version token');
    }
    const { scheduleNumber, scheduleName: name, ...schedule } = data;
    return { schedule, scheduleNumber, scheduleName: name };
  }
  // Shared handling (reauth / not-connected / not-found / generic).
  return throwForError(res, 'create schedule');
}

/**
 * Delete a D-ORCA-owned schedule. The backend cascades everything keyed to it
 * (execution records, progress-history snapshots). 204 on success; a
 * never-stored number surfaces as `ScheduleNotImportedError` (409). No UI
 * affordance consumes this yet — used programmatically (e.g. e2e teardown).
 */
export async function deleteSchedule(siteId: string, scheduleNumber: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}`,
    { method: 'DELETE' },
  );
  if (res.ok) return;
  const notImported = await throwForNotImported(res, scheduleNumber);
  if (notImported) throw notImported;
  // Shared handling (reauth / not-connected / not-found / generic).
  return throwForError(res, 'delete schedule');
}

export interface IScheduleSaveResult {
  schedule: ISchedulerData;
  /**
   * Non-fatal warnings. Always `[]` on the D-ORCA save (PUT) path now — kept for
   * shape compatibility; real warnings only come from the push-to-Genba path.
   */
  warnings: string[];
}

/** Result of a manual push-to-Genba: edits that could NOT be synced upstream. */
export interface IPushToGenbaResult {
  warnings: string[];
}

/**
 * Mirrors the backend ScheduleSaveRequestSchema fields that reach the wire.
 * D-ORCA owns the schedule, so the rich PLAN fields (assignee, workerCount,
 * notes, checklist, comments) and markers persist. Execution fields
 * (status/progressPercent/locations/log) are NOT sent — they belong to the
 * task/activity execution facet and ride their own per-task PUTs. The backend's
 * Save schema still accepts-and-ignores them for older clients; this client
 * simply omits them (the store's `bySite` tasks are plan-only).
 */
export interface IScheduleSavePayload {
  baseVersion: string;
  groups: Array<{ id: string; name: string; collapsed: boolean }>;
  tasks: ISchedulerPlanTask[];
  dependencies: Array<{
    id: string;
    predecessorId: string;
    successorId: string;
    type: string;
    lagDays: number;
  }>;
  markers: ISchedulerMarker[];
}

/**
 * The wire payload for a save (PUT to D-ORCA). Carries the PLAN working copy —
 * the rich task fields and markers — plus the optimistic-concurrency
 * `baseVersion`. Execution fields never appear (the tasks are already plan-only
 * by shape). The backend validates task color (#RRGGBB) on write but accepts
 * every plan edit kind (delete / rename / regroup / milestone conversion /
 * SS|FF|SF deps with lag).
 */
export function toSavePayload(data: ISchedulerPlanData): IScheduleSavePayload {
  return {
    baseVersion: data.version ?? '',
    groups: data.groups.map((g) => ({ id: g.id, name: g.name, collapsed: g.collapsed })),
    // Plan tasks only (assignee/workerCount/notes/checklist/comments); execution
    // fields are absent from the plan facet by shape, nothing to strip.
    tasks: data.tasks.map((t) => ({ ...t })),
    dependencies: data.dependencies.map((d) => ({
      id: d.id,
      predecessorId: d.predecessorId,
      successorId: d.successorId,
      type: d.type,
      lagDays: d.lagDays,
    })),
    markers: (data.markers ?? []).map((m) => ({ ...m })),
  };
}

/**
 * Save the edited schedule to the D-ORCA DB (fast — no Genba round-trip). Accepts
 * every edit kind. Returns the authoritative stored doc + its new version token.
 * A stale `baseVersion` → 409 SCHEDULE_CONFLICT (concurrent D-ORCA editor); a
 * deleted committed task with recorded work → 409 COMMITTED_TASK_HAS_WORK; a
 * never-imported schedule → 409 SCHEDULE_NOT_IMPORTED. The Genba-only failures
 * (LOCKED / SAVE_REJECTED) moved to `pushToGenba` and can't occur here.
 */
export async function saveSchedule(
  siteId: string,
  scheduleNumber: string,
  data: ISchedulerPlanData,
): Promise<IScheduleSaveResult> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toSavePayload(data)),
    },
  );
  if (res.ok) return (await res.json()) as IScheduleSaveResult;

  const body = await res.clone().json().catch(() => null);
  const code: string | undefined = body?.error?.code;
  // A concurrent editor, or a deletion of committed work the server refused
  // (QA F-72) — both recoverable on the page, so both are named.
  if (code === 'SCHEDULE_CONFLICT' || code === 'COMMITTED_TASK_HAS_WORK') {
    throw new ScheduleSaveError(code, body?.error?.message ?? 'save failed', []);
  }
  const notImported = await throwForNotImported(res, scheduleNumber);
  if (notImported) throw notImported;
  // Shared handling (reauth / not-connected / not-found / not-linked / generic).
  return throwForError(res, 'save schedule');
}

/** Wire shape of the immediate per-task stock-claims write response. */
export interface ITaskStockClaimsResponse {
  taskId: string;
  stockClaims: ITaskStockClaim[];
}

/**
 * Replace a task's stock claims ("Stock from CAD") through the per-task
 * immediate-write channel — an EXECUTION fact stored on the activities document
 * (ruling 9), not a plan edit, so no schedule version is involved. Whole-array
 * replace, last write wins. Web-only: the Stock tab has no mobile surface.
 */
export async function updateTaskStockClaims(
  siteId: string,
  scheduleNumber: string,
  taskId: string,
  stockClaims: ITaskStockClaim[],
): Promise<ITaskStockClaimsResponse> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/tasks/${encodeURIComponent(taskId)}/stock-claims`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stockClaims }),
    },
  );
  if (res.ok) return (await res.json()) as ITaskStockClaimsResponse;
  return throwForError(res, 'update task stock claims');
}
/**
 * Manually push the D-ORCA-stored schedule back to Genba (best-effort, Phase 3).
 * Separate from the save path: the backend pushes the expressible subset and
 * returns the edits it could NOT sync as `warnings`. Genba-specific failures
 * surface as a `ScheduleSaveError`: 423 SCHEDULE_LOCKED (web editor open),
 * 502 SCHEDULE_SAVE_REJECTED (import rejected), 409 SCHEDULE_CONFLICT (upstream
 * moved). 404 not-imported maps to `ScheduleNotImportedError`.
 */
export async function pushToGenba(
  siteId: string,
  scheduleNumber: string,
): Promise<IPushToGenbaResult> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedules/${encodeURIComponent(scheduleNumber)}/push-to-genba`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  if (res.ok) {
    const body = (await res.json()) as { warnings?: string[] };
    return { warnings: body.warnings ?? [] };
  }

  const body = await res.clone().json().catch(() => null);
  const code: string | undefined = body?.error?.code;
  if (code === 'SCHEDULE_LOCKED' || code === 'SCHEDULE_SAVE_REJECTED' || code === 'SCHEDULE_CONFLICT') {
    const details = body?.error?.details ?? {};
    const messages: string[] = details.errors ?? details.violations ?? [];
    throw new ScheduleSaveError(code, body?.error?.message ?? 'push failed', messages);
  }
  const notImported = await throwForNotImported(res, scheduleNumber);
  if (notImported) throw notImported;
  // Shared handling (reauth / not-connected / not-found / not-linked / generic).
  return throwForError(res, 'push to Genba');
}
